import { useState, useEffect, useRef } from 'react';
import { Outlet, NavLink, Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { LogIn, LogOut, User, UserCircle, Shield, BarChart3, Trophy, Calendar, Menu, X, ChevronDown } from 'lucide-react';

export function Layout() {
  const { user, isAuthenticated, isAdmin, signOut } = useAuth();
  const [menuOpen, setMenuOpen] = useState(false);
  const [profileOpen, setProfileOpen] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();
  const profileRef = useRef<HTMLDivElement>(null);

  // Close menus on route change
  useEffect(() => {
    setMenuOpen(false);
    setProfileOpen(false);
  }, [location.pathname]);

  // Close on Escape key
  useEffect(() => {
    function handleKeyDown(e: KeyboardEvent) {
      if (e.key === 'Escape') {
        setMenuOpen(false);
        setProfileOpen(false);
      }
    }
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, []);

  // Close profile dropdown on outside click
  useEffect(() => {
    function handleClickOutside(e: MouseEvent) {
      if (profileRef.current && !profileRef.current.contains(e.target as Node)) {
        setProfileOpen(false);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Prevent body scroll when mobile menu is open
  useEffect(() => {
    if (menuOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => { document.body.style.overflow = ''; };
  }, [menuOpen]);

  return (
    <div className="app-layout">
      <header className="header">
        <div className="header-inner">
          <Link to="/" className="header-logo">
            <span className="header-logo-text">
              <span style={{ whiteSpace: 'nowrap' }}>UNI <span className="accent">2026</span></span>{' '}
              <span style={{ whiteSpace: 'nowrap' }}>MATCH PLAY</span>
            </span>
          </Link>

          {/* Hamburger toggle – mobile only */}
          <button
            className="mobile-menu-toggle"
            onClick={() => setMenuOpen(!menuOpen)}
            aria-label={menuOpen ? 'Zavřít menu' : 'Otevřít menu'}
            aria-expanded={menuOpen}
          >
            {menuOpen ? <X size={22} /> : <Menu size={22} />}
          </button>

          {/* Overlay – mobile only */}
          {menuOpen && (
            <div className="mobile-menu-overlay" onClick={() => setMenuOpen(false)} />
          )}

          {/* Navigation */}
          <nav className={`header-nav ${menuOpen ? 'open' : ''}`}>
            {/* Mobile menu header */}
            <div className="mobile-menu-header">
              <img src="/logos/cgp_logo_white.png" alt="Czech Golf Production" style={{ height: '28px', objectFit: 'contain' }} />
            </div>

            {/* ── Public links (always visible) ── */}
            <NavLink to="/" end className={({ isActive }) => isActive ? 'active' : ''}>
              <Calendar size={15} />
              <span>Program</span>
            </NavLink>
            <NavLink to="/results" className={({ isActive }) => isActive ? 'active' : ''}>
              <Trophy size={15} />
              <span>Výsledky</span>
            </NavLink>
            <NavLink to="/stats" className={({ isActive }) => isActive ? 'active' : ''}>
              <BarChart3 size={15} />
              <span>Statistiky</span>
            </NavLink>

            {isAuthenticated ? (
              <>
                {/* ── Mobile-only auth links (shown in hamburger) ── */}
                <NavLink to="/profile" className={({ isActive }) => `nav-mobile-only${isActive ? ' active' : ''}`}>
                  <UserCircle size={15} />
                  <span>Profil</span>
                </NavLink>
                <NavLink to="/my-matches" className={({ isActive }) => `nav-mobile-only${isActive ? ' active' : ''}`}>
                  <User size={15} />
                  <span>Moje hry</span>
                </NavLink>
                {isAdmin && (
                  <NavLink to="/admin" className={({ isActive }) => `nav-mobile-only${isActive ? ' active' : ''}`}>
                    <Shield size={15} />
                    <span>Admin</span>
                  </NavLink>
                )}
                <button className="nav-mobile-only" onClick={async () => { await signOut(); navigate('/'); }}>
                  <LogOut size={15} />
                  <span>Odhlásit</span>
                </button>

                {/* ── Desktop profile dropdown ── */}
                <div className="profile-dropdown nav-desktop-only" ref={profileRef}>
                  <button
                    className={`profile-dropdown-trigger${profileOpen ? ' active' : ''}`}
                    onClick={() => setProfileOpen(v => !v)}
                    aria-expanded={profileOpen}
                    aria-haspopup="true"
                  >
                    <UserCircle size={15} />
                    <span>Profil</span>
                    <ChevronDown
                      size={13}
                      style={{
                        transform: profileOpen ? 'rotate(180deg)' : 'rotate(0deg)',
                        transition: 'transform 0.22s ease',
                        marginLeft: '-0.125rem',
                      }}
                    />
                  </button>

                  {profileOpen && (
                    <div className="profile-dropdown-menu">
                      <NavLink to="/profile" className={({ isActive }) => isActive ? 'active' : ''}>
                        <UserCircle size={14} />
                        <span>Profil</span>
                      </NavLink>
                      <NavLink to="/my-matches" className={({ isActive }) => isActive ? 'active' : ''}>
                        <User size={14} />
                        <span>Moje hry</span>
                      </NavLink>
                      {isAdmin && (
                        <NavLink to="/admin" className={({ isActive }) => isActive ? 'active' : ''}>
                          <Shield size={14} />
                          <span>Admin</span>
                        </NavLink>
                      )}
                      <div className="profile-dropdown-divider" />
                      <button onClick={async () => { await signOut(); setProfileOpen(false); navigate('/'); }}>
                        <LogOut size={14} />
                        <span>Odhlásit</span>
                      </button>
                    </div>
                  )}
                </div>
              </>
            ) : (
              <NavLink to="/login">
                <LogIn size={15} />
                <span>Přihlásit</span>
              </NavLink>
            )}
          </nav>
        </div>
      </header>

      <main className="main-content">
        <Outlet />
      </main>
    </div>
  );
}
